import { Styles } from '@types'
import { Radio, Form, Modal, Select, Checkbox, Space } from 'antd'
import { useForm } from 'antd/lib/form/Form'
import dayjs, { Dayjs } from 'dayjs'
import { ContractStatus } from 'gadjet-v2-types/dist/type'
import { contractStatus } from 'gadjet-v2-types/dist/type/label'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import bufferToFileDownload from '@utils/download'
import formRule from '@utils/formRule'

import ContractAPI from '@apis/branch/contract'

import { RootState } from '@reducers/index'

import LocalDatePicker from '@components/atoms/LocalDatePicker'

type Props = {
  hqId: number
  branchId: number
  onDone?: () => void
  onClose?: () => void
}

type FormValues = {
  filterType: 'period' | 'status'
  criteria?: 'startDate' | 'suspendDate'
  dateRange?: [Dayjs, Dayjs]
  status?: ContractStatus[]
}

export default function ContractExportModal({ hqId, branchId, onClose, onDone }: Props): JSX.Element {
  const [loading, setLoading] = useState(false)
  const [form] = useForm<FormValues>()
  const filterType = Form.useWatch('filterType', form)
  const branchName = useSelector((state: RootState) => state.branch.name)

  const onOk = async () => {
    const values = await form.validateFields()
    setLoading(true)

    const query: any = {}

    if (values.filterType === 'period' && values.criteria && values.dateRange) {
      query[values.criteria] = [values.dateRange[0].format('YYYY-MM-DD'), values.dateRange[1].format('YYYY-MM-DD')]
    } else if (values.filterType === 'status' && values.status) {
      query.status = values.status
    }

    const { data } = await ContractAPI.exportContracts({ hqId, branchId }, query)

    const today = dayjs().format('YYMMDD')
    const fileName = `${branchName}_계약_${today}.xlsx`
    bufferToFileDownload(fileName, Buffer.from(data))

    setLoading(false)
    if (onDone) onDone()
    if (onClose) onClose()
  }

  const initialValues = useMemo(
    () => ({
      filterType: 'period',
      criteria: 'startDate',
      dateRange: [dayjs().startOf('month'), dayjs().endOf('month')],
      status: [],
    }),
    []
  )

  const contractStatusOptions = useMemo(
    (): { label: string; value: ContractStatus }[] => [
      { label: contractStatus['before-started'], value: 'before-started' },
      { label: contractStatus.started, value: 'started' },
      { label: contractStatus.suspended, value: 'suspended' },
      { label: contractStatus.expired, value: 'expired' },
      { label: contractStatus.extended, value: 'extended' },
    ],
    []
  )

  const reset = () => {
    form.resetFields()
    setLoading(false)
  }

  useEffect(() => {
    reset()
  }, [])

  return (
    <Modal visible onOk={onOk} onCancel={onClose} title="계약 다운로드" okText="다운로드" confirmLoading={loading}>
      <Form form={form} layout="vertical" initialValues={initialValues}>
        <Form.Item label="필터 기준" name="filterType" rules={[formRule.required]}>
          <Radio.Group>
            <Radio value="period">기간</Radio>
            <Radio value="status">상태</Radio>
          </Radio.Group>
        </Form.Item>

        {filterType === 'period' && (
          <>
            <Form.Item label="기간 기준" name="criteria" rules={[formRule.required]}>
              <Radio.Group>
                <Radio value="startDate">계약 기간</Radio>
                <Radio value="suspendDate">계약 종료일</Radio>
              </Radio.Group>
            </Form.Item>
            <Form.Item label="기간" name="dateRange" rules={[formRule.required]}>
              <LocalDatePicker.RangePicker size="large" style={styles.datePicker} allowClear={false} />
            </Form.Item>
          </>
        )}

        {filterType === 'status' && (
          <>
            <div style={styles.infoBox}>
              <ul style={styles.infoList}>
                <li>검색 시점에 조회되는 계약 상태에 따라 계약 리스트가 다운로드됩니다.</li>
                <li>계약 상태는 매일 오전 0시에 갱신됩니다.</li>
              </ul>
            </div>
            <Form.Item
              label="상태"
              name="status"
              rules={[{ required: true, message: '최소 하나의 상태를 선택해주세요' }]}
            >
              <Checkbox.Group>
                {contractStatusOptions.map((option) => (
                  <Checkbox key={option.value} value={option.value}>
                    {option.label}
                  </Checkbox>
                ))}
              </Checkbox.Group>
            </Form.Item>
          </>
        )}
      </Form>
    </Modal>
  )
}

const styles: Styles = {
  datePicker: { width: '100%' },
  infoBox: {
    backgroundColor: '#fffbe6',
    border: '1px solid #ffe58f',
    borderRadius: '4px',
    padding: '10px',
    marginBottom: '16px',
  },
  infoList: {
    margin: 0,
    paddingLeft: '20px',
    color: '#595959',
    fontSize: '12px',
    lineHeight: '1.6',
  },
}
